import { refreshMunicipalPropertyManagement } from "@/domain/municipal-property/lib/refresh-municipal-property-management";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import {
  AddMunicipalPropertyYearInput,
  AddMunicipalPropertyYearMutation,
  BatchUpdateMunicipalPropertyValueInput,
  BatchUpdateMunicipalPropertyValueListMutation,
} from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";

import { ADD_MUNICIPAL_PROPERTY_YEAR_MUTATION_DOCUMENT } from "./add-municipal-property-year-mutation.gql";
import { batchUpdateMunicipalPropertyValueListMutationDocument } from "./batch-update-municipal-property-value-list-mutation.gql";

export function useMunicipalPropertyYearMutations(): {
  add: UseMutationResult<AddMunicipalPropertyYearMutation, Error, AddMunicipalPropertyYearInput>;
  updateValueList: UseMutationResult<
    BatchUpdateMunicipalPropertyValueListMutation,
    Error,
    BatchUpdateMunicipalPropertyValueInput
  >;
} {
  const queryClient: QueryClient = useQueryClient();
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();

  const add: UseMutationResult<AddMunicipalPropertyYearMutation, Error, AddMunicipalPropertyYearInput> = useMutation({
    mutationFn: (input: AddMunicipalPropertyYearInput): Promise<AddMunicipalPropertyYearMutation> =>
      createOperationClient("AddMunicipalPropertyYear").request(ADD_MUNICIPAL_PROPERTY_YEAR_MUTATION_DOCUMENT, {
        input,
      }),
    onSuccess: async function handleAdditionSuccess(
      _data: AddMunicipalPropertyYearMutation,
      input: AddMunicipalPropertyYearInput,
    ): Promise<void> {
      await refreshMunicipalPropertyManagement({
        queryClient,
        scope: {
          workspaceId: currentWorkspace.id,
          propertyIdList: [input.propertyId],
        },
      }).catch(() => undefined);
    },
  });

  const updateValueList: UseMutationResult<
    BatchUpdateMunicipalPropertyValueListMutation,
    Error,
    BatchUpdateMunicipalPropertyValueInput
  > = useMutation({
    mutationFn: (
      input: BatchUpdateMunicipalPropertyValueInput,
    ): Promise<BatchUpdateMunicipalPropertyValueListMutation> =>
      createOperationClient("BatchUpdateMunicipalPropertyValueList").request(
        batchUpdateMunicipalPropertyValueListMutationDocument,
        { input },
      ),
    onSuccess: async function handleValueUpdateSuccess(): Promise<void> {
      await refreshMunicipalPropertyManagement({
        queryClient,
        scope: { workspaceId: currentWorkspace.id, propertyIdList: [] },
      });
    },
  });

  return { add, updateValueList };
}
